import { Language, Role, TimezoneTypeEnum } from '../../../../common/constants';
import { PermissionModel } from '../../../../common/models';
import { UserOwnerType } from '../../administration/user/constants';
import { ListViewMode } from '../../monitoring/constants';

export interface UserDataRole {
  role: Role;
  organizations: number[];
}

export type AuthMeResponse = AuthUserData & {
  expiresAt?: string | null;
};

export class AuthUserData {
  roles: UserDataRole[];

  constructor(
    authUserData: ParsedTokenModel & { permissions: PermissionModel[] },
    public id: number = authUserData.user && authUserData.user.id,
    public email: string = authUserData.user && authUserData.user.email,
    public ownerType: UserOwnerType = authUserData.user && authUserData.user.ownerType,
    public permissions: PermissionModel[] = authUserData.permissions,
    public fullName: string = authUserData.user && `${authUserData.user.firstName} ${authUserData.user.lastName}`,
    public dpnAccepted: boolean = authUserData.dpnAccepted,
    public isInternalUser: boolean = authUserData.isInternalUser,
    public newsSubscribed: boolean = authUserData.newsSubscribed,
    public defaultLanguage: Language = authUserData.defaultLanguage,
    public country: string = authUserData.country,
    public timezone: string = authUserData.user?.timezone,
    public twentyFourHourTime: boolean = authUserData.twentyFourHourTime,
    public renderTimezone: TimezoneTypeEnum = authUserData.user?.renderTimezone,
    public passwordExpired: boolean = authUserData.passwordExpired,
    public isFirstLogin: boolean = authUserData.isFirstLogin,
    public authenticationIdp: AuthenticationIdpType = authUserData.authenticationIdp,
    public isMaterialView: boolean = authUserData.isMaterialView,
    public addDeliveryDateColumn: boolean = authUserData.addDeliveryDateColumn,
    public loadTOMode: ListViewMode = authUserData.loadTOMode,
  ) {
    if (authUserData.user && authUserData.user.roles) {
      this.roles = Object.keys(authUserData.user.roles).map((role: string) => {
        return {
          role,
          organizations: authUserData.user.roles[role],
        } as UserDataRole;
      });
    }
  }
}

export class ParsedTokenModel {
  country: string;
  twentyFourHourTime: boolean;
  defaultLanguage: Language;
  dpnAccepted: boolean;
  email: string;
  emailConfirmed: boolean;
  exp: number;
  iat: number;
  isInternalUser: boolean;
  id: number;
  newsSubscribed: boolean;
  passwordExpired: boolean;
  plantIds: string[];
  roles: Role[];
  user: ParsedTokenUserModel;
  isFirstLogin: boolean;
  authenticationIdp: AuthenticationIdpType;
  isMaterialView: boolean;
  addDeliveryDateColumn: boolean;
  loadTOMode: ListViewMode;
}

class ParsedTokenUserModel {
  email: string;
  firstName: string;
  id: number;
  lastName: string;
  needApproval: boolean;
  ownerType: UserOwnerType;
  renderTimezone: TimezoneTypeEnum;
  timezone: string;
  roles: ParsedTokenUserRolesModel;
}

export enum AuthenticationIdpType {
  INTRACK2 = 'intrack2',
  SKID = 'skid',
  SKIDCN = 'skid-cn',
}

interface ParsedTokenUserRoleModel {
  organizationIds: OrganizationIdsModel;
  organizationRole: boolean;
}

type ParsedTokenUserRolesModel = Record<Role, ParsedTokenUserRoleModel>;

type OrganizationIdsModel = {
  [key: number]: {
    partnerIds: number[];
    partnerSapIds: number[];
    partnerShipToIds: number[];
    partnerTmsIds: number[];
  };
};

export type MeRolesMap = Record<string, unknown>;

export interface AuthMeUserDto {
  id: number;
  email: string;
  ownerType: string;
  needApproval: boolean;
  roles: MeRolesMap;
  renderTimezone: TimezoneTypeEnum;
  timezone: string;
  firstName: string;
  lastName: string;
}

export interface AuthMeDto {
  id: number;
  email: string;
  roles: string[];
  user: AuthMeUserDto;

  emailConfirmed: boolean;
  newsSubscribed: boolean;
  dpnAccepted: boolean;
  passwordExpired: boolean;
  defaultLanguage: Language;
  country: string;
  plantIds: string[];
  isInternalUser: boolean;
  twentyFourHourTime: boolean;
  isFirstLogin: boolean;
  authenticationIdp: AuthenticationIdpType | '';
  addDeliveryDateColumn: boolean;
  isMaterialView: boolean;
  loadToMode: string;

  permissions: PermissionModel[];
  expiresAt?: string | null;
}

export interface LoginData {
  dto: AuthMeDto;
  response: AuthMeResponse;
}
